import React, { useState, useContext, useEffect } from "react";
import { Layout, PageActions } from "@shopify/polaris";

// import hoc
import { withErrorBoundary, withFeature } from "lib/hoc";
import constant from "lib/constant/constant";

//  import components
import { Banner, SkeletonAnnotated } from "lib/components";

// contexts
import { PrivateContext } from "lib/context";

// gql
import { useMutation, useQuery } from "@apollo/client";
import { ADD_VARIANT, UPLOAD_IMAGE } from "app/product/apollo/mutations";
import { GET_VENDOR_VARIANT } from "app/product/apollo/queries";

// helpers
import { baseHelper, errorHelper } from "lib/helpers";

// sub feature items
import { useNavigate } from "react-router-dom";
import { Image, Title, Pricing, Inventory, Shipping } from "./subFeatureItems";

const AdminVariantAdd = () => {
  const { cms } = useContext(PrivateContext);
  const navigate = useNavigate();
  const { productId = "" } = useParams();
  const {
    gql,
    value: { MAX_FLAT, MIN_PRICE },
  } = constant;

  const { data: variantData, loading, error } = useQuery(GET_VENDOR_VARIANT, {
    variables: { input: { id: productId } },
  });
  const [addVariant, { loading: addVariantLoading }] = useMutation(ADD_VARIANT);
  const [uploadImage, { loading: imageLoading }] = useMutation(UPLOAD_IMAGE);

  // states
  const [banner, setBanner] = useState({
    action: null,
    children: null,
    isOpen: false,
    status: "",
    title: "",
  });
  const [title, setTitle] = useState({});
  const [image, setImage] = useState("");
  const [pricing, setPricing] = useState({
    price: "",
    comparePrice: "",
  });
  const [inventory, setInventory] = useState({
    sku: "",
    barCode: "",
    inventoryPolicy: "",
    quantity: "",
  });
  const [shipping, setShipping] = useState({
    isPhysicalProduct: true,
    weight: "",
    weightUnit: "lb",
  });
  const [variantOption, setVariantOption] = useState([]);
  const [titleError, setTitleError] = useState({});

  const acceptOnlyValidInput = (value, prevValue, max = MAX_FLAT, min = MIN_PRICE) => {
    if (value >= min && value <= max) {
      return (baseHelper.validatePositiveNumericValues(value) && value) || (value !== "" && prevValue) || "";
    }
    if (!value) {
      return "";
    }
    return prevValue;
  };

  useEffect(() => {
    if (error && Object.keys(error).length) {
      setBanner((prev) => ({
        ...prev,
        isOpen: true,
        status: constant.CRITICAL,
        title: cms("message.error.fetch"),
      }));
    }
  }, [cms, error]);

  if (variantData && Object.keys(variantOption).length === 0) {
    const responseData = baseHelper.getResponseData(variantData, gql.GET_PRODUCT);
    const responseError = baseHelper.getResponseError(variantData, gql.GET_PRODUCT);
    const { variants = [] } = responseData || {};
    if (responseError) {
      setBanner({
        ...banner,
        isOpen: true,
        status: constant.CRITICAL,
        title: responseError,
      });
    }
    let options = {
      option1: cms("label.size"),
      option2: cms("label.color"),
      option3: cms("label.material"),
    };
    if (variants.length) {
      const {
        option1Val = null,
        option2Val = null,
        option3Val = null,
        option1 = null,
        option2 = null,
        option3 = null,
      } = variants[0];
      const availableOptions = {
        option1: option1Val && option1,
        option2: option2Val && option2,
        option3: option3Val && option3,
      };
      options = availableOptions;
    }
    setVariantOption(options);
  }

  const onDismissBanner = () => {
    setBanner({
      action: null,
      children: null,
      isOpen: false,
      status: "",
      title: "",
    });
  };

  if (loading) {
    return <SkeletonAnnotated />;
  }

  const saveVariant = async (variantValue) => {
    try {
      const result = await addVariant({
        variables: {
          input: variantValue,
        },
      });

      const responseData = baseHelper.getResponseData(result.data, gql.ADD_VARIANT);
      const responseError = baseHelper.getResponseError(result.data, gql.ADD_VARIANT);

      if (responseError) {
        setBanner({
          isOpen: true,
          status: constant.CRITICAL,
          title: responseError,
        });
        return;
      }
      if (responseData) {
        navigate(`/variants/edit/${productId}/${responseData.position}`, { addVariant: true });
      }
    } catch (exception) {
      setBanner((prev) => ({
        ...prev,
        isOpen: true,
        status: constant.CRITICAL,
        title: errorHelper.parse(exception),
      }));
    }
  };

  const prepareData = () => {
    const { option1 = null, option2 = null, option3 = null } = title;
    const formValues = {
      productId,
      seller: "",
      image: {},
      option1Val: option1 || "",
      option2Val: option2 || "",
      option3Val: option3 || "",
      price: parseFloat(pricing.price),
      comparePrice: parseFloat(pricing.comparePrice),
      sku: inventory.sku,
      barcode: inventory.barCode,
      inventoryManagement: inventory.inventoryPolicy,
      inventoryQuantity: parseInt(inventory.quantity, 10),
      isShipping: shipping.isPhysicalProduct,
      weight: parseFloat(shipping.weight),
      weightUnit: (shipping.weight && shipping.weight !== "" && shipping.weightUnit) || constant.weightValue.LB,
    };

    const requiredError = {};
    let hasError = false;

    if (variantOption.option1) {
      requiredError.option1 = !(option1 && option1.trim() !== "");
      hasError = hasError || requiredError.option1;
    }
    if (variantOption.option2) {
      requiredError.option2 = !(option2 && option2.trim() !== "");
      hasError = hasError || requiredError.option2;
    }
    if (variantOption.option3) {
      requiredError.option3 = !(option3 && option3.trim() !== "");
      hasError = hasError || requiredError.option3;
    }
    setTitleError(requiredError);

    return !hasError && formValues;
  };

  const onSubmit = async () => {
    const variantValue = prepareData();

    if (!variantValue) {
      window.scrollTo({
        top: 0,
        left: 0,
        behavior: "smooth",
      });
      return;
    }

    if (image && image.length) {
      try {
        const response = await uploadImage({ variables: { input: { image: image[0] } } });
        const responseData = baseHelper.getResponseData(response.data, gql.UPLOAD_IMAGE);
        const responseError = baseHelper.getResponseError(response.data, gql.UPLOAD_IMAGE);
        if (responseError) {
          setBanner((prev) => ({
            ...prev,
            isOpen: true,
            status: constant.CRITICAL,
            title: responseError,
          }));
          return;
        }
        const { imageUrl = null } = responseData || {};
        if (!imageUrl) {
          setBanner((prev) => ({
            ...prev,
            isOpen: true,
            status: constant.CRITICAL,
            title: cms("message.error.url"),
          }));
          return;
        }
        variantValue.image = { url: imageUrl };
      } catch (exception) {
        setBanner((prev) => ({
          ...prev,
          isOpen: true,
          status: constant.CRITICAL,
          title: errorHelper.parse(exception),
        }));
        return;
      }
    }

    saveVariant(variantValue);
  };

  return (
    <>
      {banner.isOpen && (
        <Layout.Section>
          <Banner
            title={banner.title}
            status={banner.status}
            isOpen={banner.isOpen}
            action={banner.action}
            onDismiss={onDismissBanner}
          >
            {banner.children}
          </Banner>
        </Layout.Section>
      )}
      <Layout.AnnotatedSection title={cms("title")} description={cms("description")}>
        <br />
        <Title data={title} onChange={setTitle} variantOption={variantOption} error={titleError} cms={cms} />
        <Image variantImage={image} onChange={setImage} cms={cms} />
        <Pricing acceptOnlyValidInput={acceptOnlyValidInput} data={pricing} onChange={setPricing} cms={cms} />
        <Inventory data={inventory} onChange={setInventory} cms={cms} />
        <Shipping acceptOnlyValidInput={acceptOnlyValidInput} data={shipping} onChange={setShipping} cms={cms} />
        <PageActions
          primaryAction={{
            id: "submit",
            content: cms("common.button.submit"),
            onAction: () => onSubmit(),
            loading: addVariantLoading || imageLoading,
          }}
          secondaryActions={{
            id: "cancel",
            content: cms("common.button.cancel"),
            onAction: () => navigate(`/products/edit/${productId}?tab=2`),
          }}
        />
      </Layout.AnnotatedSection>
    </>
  );
};

export default withFeature(withErrorBoundary(AdminVariantAdd), { feature: constant.ADD_VARIANT });
